// @ts-nocheck

/* ------------------------------------------------
 *  Intercept pointerleave on <li> items that hold
 *  CTR mega panels so the theme framework doesn't
 *  close the menu while the cursor travels toward
 *  the overlay panel (which lives outside the <li>).
 *  Registered on `window` (capture) so it fires
 *  BEFORE the theme's `document` capture listener.
 * ------------------------------------------------ */
window.addEventListener(
  "pointerleave",
  (event) => {
    const target = event.composedPath?.()?.[0] ?? event.target;
    if (!(target instanceof Element)) return;
    if (!target.matches?.(".menu-list__list-item")) return;

    // Overflow items have panels inside the <li> — no interception needed
    if (target.slot === "overflow") return;

    const source = document.querySelector(
      ".ctr-mega-menu-source[data-ctr-active]"
    );
    if (!source) return;

    const activeTitle = source.getAttribute("data-ctr-active");
    const linkEl = target.querySelector(".menu-list__link-title");
    if (!linkEl) return;

    const linkText = linkEl.textContent.trim().toLowerCase();
    if (linkText !== activeTitle) return;

    const panel = source.querySelector(
      `.ctr-mega-menu-source__panel[data-nav-title="${activeTitle}"]`
    );
    if (!panel) return;

    const panelRect = panel.getBoundingClientRect();
    if (panelRect.height <= 0) return;

    const liRect = target.getBoundingClientRect();
    const cursorBelowLink = event.clientY >= liRect.bottom - 5;
    const cursorAboveEnd = event.clientY <= panelRect.bottom;
    const cursorInX =
      event.clientX >= panelRect.left && event.clientX <= panelRect.right;

    if (cursorBelowLink && cursorAboveEnd && cursorInX) {
      event.stopImmediatePropagation();
    }
  },
  true
);

/* ----------------------------------------
 * Global cleanup for Shopify design-mode
 * ---------------------------------------- */
function ctrMegaCleanupHeader() {
  document.querySelectorAll("[data-ctr-has-panel]").forEach((el) => {
    el.removeAttribute("data-ctr-has-panel");
  });

  document
    .querySelectorAll(".ctr-mega-menu-source__panel[data-ctr-in-overflow]")
    .forEach((el) => el.remove());

  document
    .querySelectorAll("header-menu > .ctr-mega-menu-source")
    .forEach((el) => el.remove());
}

/* ----------------------------------------------------------------------------
 * ITEM 90 (15 Sep 2026): in the theme editor, editing the header emptied every panel.
 *
 * _setup() moves the source INTO <header-menu>, which belongs to the Header section,
 * not to this one. A header re-render threw the source away with the old header, and
 * this file then ran ctrMegaCleanupHeader() on EVERY section:load and section:unload -
 * any section's, not only this one's - so nothing remounted it until the preview
 * reloaded. Only this section's own init() ever mounted a source.
 *
 * Design mode only, now:
 *   - this section's own unload removes the old source; its new render mounts a new one,
 *   - an unload of the section that currently HOLDS the source (the header) parks the
 *     source back in its own section, observers disconnected, panels moved home,
 *   - any section:load remounts a parked source into whichever <header-menu> is there,
 *   - destroy() parks too, for the case where the header is removed before its unload
 *     event: re-adding the element re-initialises the component, which mounts it.
 * -------------------------------------------------------------------------- */
function ctrMegaIsHomeSection(source, sectionId) {
  const home = source.__ctrMegaHome;
  return !!home && !!sectionId && home.id === `shopify-section-${sectionId}`;
}

if (typeof Shopify !== "undefined" && Shopify.designMode) {
  document.addEventListener("shopify:section:unload", (event) => {
    const section = event.target instanceof Element ? event.target : null;
    const sectionId = event.detail && event.detail.sectionId;
    document.querySelectorAll("header-menu > .ctr-mega-menu-source").forEach((source) => {
      if (!source.__ctrMegaPark || ctrMegaIsHomeSection(source, sectionId)) {
        ctrMegaCleanupHeader();
      } else if (section && section.contains(source)) {
        source.__ctrMegaPark(true);
      }
    });
  });

  document.addEventListener("shopify:section:load", () => {
    document
      .querySelectorAll(".ctr-mega-menu-source[data-ctr-parked]")
      .forEach((source) => source.__ctrMegaRemount && source.__ctrMegaRemount());
  });
}

/* ----------------------------------------------------------------------------
 * KEYBOARD ACCESS TO THE MAIN-BAR PANELS. Round CSS4 finding 1, 13 Sep 2026.
 *
 * A main-bar panel is appended to the END of <header-menu> by _setup(), not
 * inside its <li>, so Tab order runs link -> next link and never enters the
 * open panel. Measured on live before this: Tab onto SHOP opened its panel,
 * the next Tab went to JUICE CLEANSE and no link inside any panel ever took
 * focus. Neither header-menu.js nor this file handled a single key.
 *
 * Overflow items are NOT handled here: their panel is moved inside the <li>
 * (_moveToOverflow), so ordinary Tab order already reaches it.
 *
 *   Tab on an open item's link   -> first link in its panel
 *   ArrowDown on an item's link  -> opens it if needed, then first link in its panel
 *   Tab on the panel's last link -> the next top-level link
 *   Shift+Tab on its first link  -> back to the item's own link
 *   Escape in the panel or link  -> closes the panel, focus on the item's link
 *   focus leaving panel and menu -> closes the panel
 *
 * Everything is delegated on <header-menu> and bound once per element, so a
 * design-mode re-init does not stack listeners. It reuses the two levers the
 * pointer path already relies on: headerMenu.activate() to cancel a pending
 * close, and a synthetic pointerleave on the <li> to request one
 * (component.js routes pointerleave to the element carrying on:pointerleave).
 * -------------------------------------------------------------------------- */
const CTR_MEGA_FOCUSABLE =
  'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function ctrMegaBindKeyboard(headerMenu) {
  if (!headerMenu || headerMenu.__ctrMegaKeyboard) return;
  headerMenu.__ctrMegaKeyboard = true;

  const itemFor = (link) => link.closest(".menu-list__list-item");

  // Compare titles in JS, never inside a selector - see the CSS.escape note in _setup.
  const panelFor = (li) => {
    if (!li || li.slot === "overflow") return null;
    const title = li.getAttribute("data-ctr-has-panel");
    if (!title) return null;
    return (
      [...headerMenu.querySelectorAll(".ctr-mega-menu-source > .ctr-mega-menu-source__panel")].find(
        (p) => p.getAttribute("data-nav-title") === title
      ) || null
    );
  };

  const itemForPanel = (panel) => {
    const title = panel.getAttribute("data-nav-title");
    return (
      [...headerMenu.querySelectorAll(".menu-list__list-item[data-ctr-has-panel]")].find(
        (li) => li.getAttribute("data-ctr-has-panel") === title
      ) || null
    );
  };

  const focusables = (panel) =>
    panel && panel.hasAttribute("data-ctr-visible")
      ? [...panel.querySelectorAll(CTR_MEGA_FOCUSABLE)].filter(
          (el) => el.getClientRects().length && el.tabIndex >= 0 && !el.closest('[aria-hidden="true"]')
        )
      : [];

  const topLinks = () =>
    [...headerMenu.querySelectorAll(".menu-list__list-item > .menu-list__link")].filter(
      (a) => a.getClientRects().length
    );

  const requestClose = (li) => {
    li?.dispatchEvent(new PointerEvent("pointerleave", { bubbles: false }));
  };

  const focusFirst = (link) => {
    const first = focusables(panelFor(itemFor(link)))[0];
    if (first) {
      first.focus();
      return true;
    }
    return false;
  };

  // Wait for header-menu.js to open the item (it debounces 0-250 ms), then the
  // panel-sync observer in _observe() marks the panel visible in the same task.
  const whenOpen = (link, cb) => {
    if (link.getAttribute("aria-expanded") === "true") return void requestAnimationFrame(cb);
    const mo = new MutationObserver(() => {
      if (link.getAttribute("aria-expanded") !== "true") return;
      mo.disconnect();
      clearTimeout(timer);
      requestAnimationFrame(cb);
    });
    mo.observe(link, { attributes: true, attributeFilter: ["aria-expanded"] });
    const timer = setTimeout(() => mo.disconnect(), 1000);
  };

  headerMenu.addEventListener("keydown", (event) => {
    if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
    const target = event.target;
    if (!(target instanceof Element)) return;

    const link = target.closest(".menu-list__list-item > .menu-list__link");
    if (link) {
      const li = itemFor(link);
      const panel = panelFor(li);
      if (!panel) return;

      if (event.key === "Escape" && link.getAttribute("aria-expanded") === "true") {
        event.preventDefault();
        requestClose(li);
        return;
      }

      if (event.key === "Tab" && !event.shiftKey && link.getAttribute("aria-expanded") === "true") {
        if (focusFirst(link)) event.preventDefault();
        return;
      }

      if (event.key === "ArrowDown") {
        event.preventDefault();
        if (link.getAttribute("aria-expanded") !== "true") headerMenu.activate?.({ target: li });
        whenOpen(link, () => {
          if (focusFirst(link)) return;
          // Off the homepage the panel markup is fetched on first intent; try once more when it lands.
          window.__ctrMegaPanelsRequest?.then(() =>
            requestAnimationFrame(() => {
              if (link.getAttribute("aria-expanded") === "true" && document.activeElement === link) focusFirst(link);
            })
          );
        });
      }
      return;
    }

    const panel = target.closest(".ctr-mega-menu-source > .ctr-mega-menu-source__panel");
    if (!panel) return;
    const li = itemForPanel(panel);
    const ownLink = li?.querySelector(":scope > .menu-list__link");
    if (!li || !ownLink) return;

    if (event.key === "Escape") {
      event.preventDefault();
      ownLink.focus();
      requestClose(li);
      return;
    }

    if (event.key !== "Tab") return;
    const list = focusables(panel);
    if (!list.length) return;

    if (event.shiftKey && target === list[0]) {
      event.preventDefault();
      ownLink.focus();
    } else if (!event.shiftKey && target === list[list.length - 1]) {
      const links = topLinks();
      const next = links[links.indexOf(ownLink) + 1];
      if (next) {
        event.preventDefault();
        next.focus();
      }
    }
  });

  // Focus inside a panel keeps it open: the item's own blur has already queued a close.
  headerMenu.addEventListener("focusin", (event) => {
    const panel =
      event.target instanceof Element && event.target.closest(".ctr-mega-menu-source > .ctr-mega-menu-source__panel");
    if (panel) headerMenu.activate?.(new PointerEvent("pointermove"));
  });

  // Focus leaving a panel for anywhere that is neither the panel nor a menu item closes it.
  headerMenu.addEventListener("focusout", (event) => {
    const panel =
      event.target instanceof Element && event.target.closest(".ctr-mega-menu-source > .ctr-mega-menu-source__panel");
    if (!panel) return;
    const to = event.relatedTarget;
    if (to instanceof Element && (panel.contains(to) || to.closest(".menu-list__list-item"))) return;
    requestClose(itemForPanel(panel));
  });
}

document.addEventListener("alpine:init", () => {
  Alpine.data("ctrMegaScroll", function () {
    return {
      hasOverflow: false,
      canScrollLeft: false,
      canScrollRight: false,

      init() {
        this.$nextTick(() => this.updateArrows());
      },

      updateArrows() {
        const el = this.$refs.track;
        if (!el) return;
        this.hasOverflow = el.scrollWidth > el.clientWidth + 2;
        this.canScrollLeft = el.scrollLeft > 2;
        this.canScrollRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 2;
      },

      scrollBy(dir) {
        const el = this.$refs.track;
        if (!el) return;
        const child = el.firstElementChild;
        const step = child
          ? child.offsetWidth + parseInt(getComputedStyle(el).gap || 0)
          : el.clientWidth * 0.8;
        el.scrollBy({ left: dir * step, behavior: "smooth" });
      },
    };
  });

  Alpine.data("ctrMegaMenu", function () {
    return {
      _observer: null,
      _slotObserver: null,
      _panelMap: null,

      init() {
        this.$el.removeAttribute("data-ctr-parked");
        this.$el.__ctrMegaPark = (markParked) => this._park(markParked);
        this.$el.__ctrMegaRemount = () => this._remount();
        ctrMegaCleanupHeader();
        this._setup();
        this._observe();
        this._refreshScrollComponents();
      },

      _refreshScrollComponents() {
        this.$nextTick(() => {
          this.$el
            .querySelectorAll("[x-ref='track']")
            .forEach((track) => {
              const scrollEl = track.closest("[x-data='ctrMegaScroll']");
              if (!scrollEl) return;
              const data = Alpine.$data(scrollEl);
              if (data && data.updateArrows) data.updateArrows();
            });
        });
      },

      /* --------------------------------------------------------------
       * R32: panel contents ship inside <template data-ctr-mega-panel>.
       * Clone + insert + Alpine.initTree, once per panel, BEFORE the panel is
       * shown -- syncPanel() reads panel.offsetHeight in a rAF straight after
       * making it visible, and an un-hydrated panel measures 0.
       *
       * Fails open: no template (design mode, or already inline) marks the
       * panel hydrated and does nothing.
       * ------------------------------------------------------------- */
      _hydratePanel(panel) {
        if (!panel || panel.hasAttribute("data-ctr-hydrated")) return;

        const tpl = panel.querySelector(":scope > template[data-ctr-mega-panel]");
        /* THE HYDRATED FLAG IS SET ONLY ONCE THERE IS SOMETHING TO HYDRATE, and moving
           it below this check is a P1 fix, not a tidy-up. Codex round, 2 Sep 2026.

           It used to be set on the line above. With remote panels that is fatal: a panel
           hydrated BEFORE the fetch lands gets flagged, returns here because there is no
           template yet, and is then skipped forever by the guard on the first line - so
           the injected template is never cloned and the panel stays permanently empty,
           with --submenu-height written as 0px.

           It is not a rare race. It happens deterministically for an item moved into the
           overflow submenu before first intent, and for main-slot items whenever the
           fetch outlasts header-menu.js's 250 ms activation delay.

           Leaving the flag unset means a later hover simply tries again, which is the
           behaviour that was always intended. */
        if (!tpl) return;

        panel.setAttribute("data-ctr-hydrated", "");

        let first = null;
        try {
          const frag = tpl.content.cloneNode(true);
          first = frag.firstElementChild;
          panel.insertBefore(frag, tpl);
        } catch (e) {
          return;
        }

        try {
          if (window.Alpine && first) Alpine.initTree(first);
        } catch (e) {
          /* markup is in the DOM either way -- worst case the scroll arrows
             stay static, which is far better than an empty panel */
        }

        // the arrows were never measured at init, so measure them now
        requestAnimationFrame(() => {
          try {
            const track = panel.querySelector("[x-ref='track']");
            const scrollEl = track && track.closest("[x-data='ctrMegaScroll']");
            if (!scrollEl) return;
            const data = Alpine.$data(scrollEl);
            if (data && data.updateArrows) data.updateArrows();
          } catch (e) {}
        });
      },

      _setup() {
        const headerMenu = document.querySelector("header-menu");
        if (!headerMenu) return;

        // Already inside THIS header-menu. Compared by parent rather than by the old
        // data-ctr-mounted flag, so a source parked out of a re-rendered header can mount again.
        if (this.$el.parentElement === headerMenu) return;
        if (!this.$el.__ctrMegaHome) this.$el.__ctrMegaHome = this.$el.closest(".shopify-section");
        this.$el.setAttribute("data-ctr-mounted", "");

        headerMenu.appendChild(this.$el);

        /* ------------------------------------------------------------------
           REMOTE PANELS: fetch the panel markup on first intent, then inject it
           so _hydratePanel finds a <template> exactly as it always has.

           Off the homepage, blocks/_ctr-mega-panel.liquid renders only the outer
           <div data-ctr-panel-remote="<section id>"> and NOT the <template>,
           which saves 65.9 ms of Liquid on every page that is not `/`. Measured
           by Theme Inspector 2 Sep 2026.

           WHY IT INJECTS UP FRONT RATHER THAN INSIDE _hydratePanel. The obvious
           shape is to make _hydratePanel async and await the fetch. That BREAKS
           two synchronous measurement dependencies it is not obvious you are
           touching: the overflow path hydrates before moving a panel into the
           submenu so it measures a full panel (:346), and the main path hydrates
           before an offsetHeight read that sets --submenu-height (:479). Await
           anywhere in that chain measures an empty shell. So _hydratePanel is
           left EXACTLY as it was - synchronous, no signature change, no callers
           touched - and the template is simply present before it runs.

           The fetch targets `/` DELIBERATELY, not the current page: a section
           fetched through the Section Rendering API runs the same Liquid as the
           page copy, and Liquid cannot read query parameters, so the panels are
           only populated on the one path the Liquid renders them on.

           ONE request serves all five panels, held on window so repeated hovers
           do not repeat it. On failure the promise is cleared so a later hover
           retries, and the panels stay empty in the meantime - the nav links
           themselves still navigate, which is the important part.
           ------------------------------------------------------------------ */
        const remotePanels = this.$el.querySelectorAll("[data-ctr-panel-remote]");
        if (remotePanels.length) {
          const sectionId = remotePanels[0].getAttribute("data-ctr-panel-remote");
          const self = this;
          const loadRemote = () => {
            if (window.__ctrMegaPanelsRequest) return window.__ctrMegaPanelsRequest;
            window.__ctrMegaPanelsRequest = fetch(
              "/?section_id=" + encodeURIComponent(sectionId),
              { credentials: "same-origin" }
            )
              .then((r) => {
                if (!r.ok) throw new Error("mega panels " + r.status);
                return r.text();
              })
              .then((html) => {
                const doc = new DOMParser().parseFromString(html, "text/html");
                doc
                  .querySelectorAll(".ctr-mega-menu-source__panel[data-ctr-panel-key]")
                  .forEach((remote) => {
                    const tpl = remote.querySelector(
                      ":scope > template[data-ctr-mega-panel]"
                    );
                    if (!tpl) return;
                    /* Match on data-ctr-panel-key, NEVER data-nav-title. This init
                       rewrites data-nav-title lowercased to match the nav item text
                       (:206), so the page copy reads "glp-1 support" while the fetched
                       copy still reads "GLP-1 Support" and nothing matches. Measured:
                       five titles, zero matches. block.id is identical in both. */
                    const key = remote.getAttribute("data-ctr-panel-key");
                    /* Compare the attribute in JS rather than building a selector.
                       An earlier version used
                         `[data-nav-title="${CSS.escape(title)}"]`
                       which silently matched NOTHING: CSS.escape is for IDENTIFIERS,
                       so "GLP-1 Support" became "GLP-1\ Support", and a backslash
                       inside a quoted attribute value does not match the literal.
                       The fetch was fine and five panels came back with templates -
                       the selector just never found their local counterparts. */
                    document
                      .querySelectorAll(".ctr-mega-menu-source__panel[data-ctr-panel-key]")
                      .forEach((local) => {
                        if (local.getAttribute("data-ctr-panel-key") !== key) return;
                        if (local.querySelector(":scope > template[data-ctr-mega-panel]")) return;
                        local.appendChild(document.importNode(tpl, true));
                      });
                  });

                /* A PANEL THAT IS ALREADY OPEN WHEN THE RESPONSE ARRIVES MUST BE
                   HYDRATED AND RE-MEASURED HERE. Codex round 2, P1.

                   The earlier fix stopped an early _hydratePanel() call flagging a
                   panel permanently, so a LATER hover recovers it. That is not
                   enough: if the fetch outlasts header-menu.js's 250 ms activation
                   delay the panel is ALREADY OPEN, and appending the template does
                   not hydrate it or re-run the height measurement - the customer
                   sees an empty panel at --submenu-height: 0px and has to leave and
                   come back. Reduced, not fixed, which is exactly what Codex said.

                   Injection is the only moment that knows content has arrived, so it
                   hydrates whatever is on screen and rewrites the height itself. */
                document
                  .querySelectorAll(".ctr-mega-menu-source__panel[data-ctr-visible]")
                  .forEach((open) => {
                    try {
                      self._hydratePanel(open);
                      requestAnimationFrame(() => {
                        const h = open.offsetHeight;
                        if (h) headerMenu.style.setProperty("--submenu-height", `${h}px`);
                      });
                    } catch (e) {
                      /* an empty panel is bad, a thrown error mid-menu is worse */
                    }
                  });
              })
              .catch(() => {
                window.__ctrMegaPanelsRequest = null; // let a later hover retry
              });
            return window.__ctrMegaPanelsRequest;
          };

          headerMenu.addEventListener("pointerenter", loadRemote, { once: true, passive: true });
          headerMenu.addEventListener("touchstart", loadRemote, { once: true, passive: true });
          headerMenu.addEventListener("focusin", loadRemote, { once: true });
        }

        const panels = this.$el.querySelectorAll("[data-nav-title]");
        const menuItems = headerMenu.querySelectorAll(".menu-list__list-item");

        const colorClass = [...this.$el.classList].find((c) =>
          c.startsWith("color-")
        );

        this._panelMap = new Map();

        panels.forEach((panel) => {
          const navTitle = panel
            .getAttribute("data-nav-title")
            .trim()
            .toLowerCase();

          panel.setAttribute("data-nav-title", navTitle);

          if (colorClass) panel.classList.add(colorClass);

          for (const item of menuItems) {
            const linkEl = item.querySelector(
              ".menu-list__link .menu-list__link-title"
            );
            if (!linkEl) continue;

            const linkText = linkEl.textContent.trim().toLowerCase();
            if (linkText !== navTitle) continue;

            item.setAttribute("data-ctr-has-panel", navTitle);

            this._panelMap.set(navTitle, { panel, li: item });
            break;
          }
        });

        this._bindPanelPointerEvents(headerMenu);
        ctrMegaBindKeyboard(headerMenu);

        // Move panels into overflow submenu for items already in overflow
        for (const [navTitle, { li }] of this._panelMap) {
          if (li.slot === "overflow") {
            this._moveToOverflow(navTitle);
          }
        }

        // Watch for items moving to/from overflow on resize
        this._slotObserver = new MutationObserver((mutations) => {
          for (const m of mutations) {
            if (m.attributeName !== "slot") continue;
            const li = m.target;
            const navTitle = li.getAttribute("data-ctr-has-panel");
            if (!navTitle) continue;

            if (li.slot === "overflow") {
              this._moveToOverflow(navTitle);
            } else {
              this._moveToSource(navTitle);
            }
          }
        });

        for (const { li } of this._panelMap.values()) {
          this._slotObserver.observe(li, {
            attributes: true,
            attributeFilter: ["slot"],
          });
        }
      },

      _moveToOverflow(navTitle) {
        const entry = this._panelMap.get(navTitle);
        if (!entry) return;

        const { panel, li } = entry;

        // Hygiene (24 Aug 2026): clear the source-overlay visibility flag BEFORE the
        // bail below. This used to sit after it, so a panel that was visible in the
        // source and then failed to find its submenu container stayed marked visible
        // while the move silently aborted. Clearing first is correct either way -- the
        // panel is leaving the source overlay regardless of whether the move lands.
        panel.removeAttribute("data-ctr-visible");

        const submenuInner = li.querySelector(".menu-list__submenu-inner");
        if (!submenuInner) return;

        // R32: the theme reveals overflow panels itself, so this is the only
        // reliable hook. Hydrate BEFORE the move so the submenu measures a
        // full panel rather than an empty shell.
        this._hydratePanel(panel);

        panel.setAttribute("data-ctr-in-overflow", "");
        submenuInner.appendChild(panel);
      },

      /**
       * Hygiene (24 Aug 2026): `--submenu-height` was set in _observe() and never
       * unset, so it kept the last-opened panel's height on `header-menu` after the
       * menu closed, after a panel moved back to source, and after destroy(). It is
       * NOT the white block -- that was the hero <source> grid item, fixed in
       * sections/hero.liquid -- and the stale value measured a constant 56px under
       * test. Clearing it is correctness, not a repair.
       */
      _clearSubmenuHeight() {
        const headerMenu = document.querySelector("header-menu");
        if (headerMenu) headerMenu.style.removeProperty("--submenu-height");
      },

      _moveToSource(navTitle) {
        const entry = this._panelMap.get(navTitle);
        if (!entry) return;

        const { panel } = entry;

        panel.removeAttribute("data-ctr-in-overflow");
        panel.removeAttribute("data-ctr-visible");
        this._clearSubmenuHeight();
        this.$el.appendChild(panel);
      },

      _bindPanelPointerEvents(headerMenu) {
        /* THE MENU WOULD NOT CLOSE ON EVERY TEMPLATE BUT `product`, 18-19 Sep 2026.
           `activate` is an instance field on the HeaderMenu class, so it exists only after
           the custom element has been UPGRADED, which needs `header-menu.js` to have run.
           That file is a `<body>` module. Until R-ALPINE-EARLY this file was a `<body>`
           script below it, so by the time Alpine called init() the element was upgraded and
           this guard never fired. `160d2ec` moved this file into `<head>` via
           snippets/ctr-alpine-early.liquid for every template EXCEPT `product`, deferred
           scripts run in document order, and `<head>` comes first - so on the homepage, every
           collection, every page, the blog, cart and search, this returned here and NONE of
           the panel pointer handlers below were ever attached.

           What the customer got: hover SHOP, the panel opens, move the cursor off it and the
           panel never closes, because the only thing that asks the theme to deactivate when
           the cursor leaves an overlay panel is the `pointerleave` handler below. Measured on
           live 19 Sep at 1440px: homepage still open 2 s after leaving the panel with
           `data-ctr-active="shop"` and the panel at 551px, against the identical sequence on
           a product page closing to 0px. `ctrMegaBindKeyboard` was unaffected - it calls
           `headerMenu.activate?.()` lazily rather than testing it at bind time, which is why
           keyboard access kept working and hid how broad this was.

           Waiting for the definition rather than moving the script back keeps R-ALPINE-EARLY's
           head position AND makes the binding independent of script order in both directions.
           `customElements.define()` upgrades existing elements synchronously, so by the time
           this promise resolves the element in the document has its instance fields. */
        if (!headerMenu.activate) {
          if (!customElements.get("header-menu")) {
            customElements.whenDefined("header-menu").then(() => {
              const upgraded = document.querySelector("header-menu");
              if (upgraded && upgraded.activate) this._bindPanelPointerEvents(upgraded);
            });
          }
          return;
        }

        // Item 90: a remount binds again, so the previous binding is aborted first.
        if (this.$el.__ctrMegaPointerAbort) this.$el.__ctrMegaPointerAbort.abort();
        this.$el.__ctrMegaPointerAbort = new AbortController();
        const { signal } = this.$el.__ctrMegaPointerAbort;

        let throttle = 0;
        const cancelPendingClose = () => {
          headerMenu.activate(new PointerEvent("pointermove"));
        };

        this.$el
          .querySelectorAll(".ctr-mega-menu-source__panel")
          .forEach((panel) => {
            panel.addEventListener("pointerenter", () => {
              this._hydratePanel(panel); // R32 backstop
              cancelPendingClose();
            }, { signal });
            panel.addEventListener(
              "pointermove",
              () => {
                const now = Date.now();
                if (now - throttle < 200) return;
                throttle = now;
                cancelPendingClose();
              },
              { passive: true, signal }
            );
            panel.addEventListener("pointerleave", (e) => {
              // Overflow panels are inside the <li> — theme handles deactivation
              if (panel.hasAttribute("data-ctr-in-overflow")) return;

              const toEl = e.relatedTarget;
              if (
                toEl instanceof Element &&
                toEl.closest(".menu-list__list-item, .ctr-mega-menu-source__panel")
              )
                return;

              const activeLink = headerMenu.querySelector(
                ".menu-list__link[aria-expanded='true']"
              );
              if (!activeLink) return;

              const li = activeLink.closest(".menu-list__list-item");
              if (!li) return;

              li.dispatchEvent(
                new PointerEvent("pointerleave", {
                  bubbles: false,
                  clientX: e.clientX,
                  clientY: e.clientY,
                  relatedTarget: e.relatedTarget,
                })
              );
            }, { signal });
          });
      },

      _observe() {
        const headerMenu = document.querySelector("header-menu");
        if (!headerMenu) return;

        const source = this.$el;

        const syncPanel = () => {
          const activeLink = headerMenu.querySelector(
            ".menu-list__list-item[data-ctr-has-panel] .menu-list__link[aria-expanded='true']"
          );

          source
            .querySelectorAll("[data-ctr-visible]")
            .forEach((p) => p.removeAttribute("data-ctr-visible"));

          if (!activeLink) {
            source.removeAttribute("data-ctr-active");
            this._clearSubmenuHeight();
            return;
          }

          const li = activeLink.closest(".menu-list__list-item");
          const navTitle = li?.getAttribute("data-ctr-has-panel");
          if (!navTitle) {
            source.removeAttribute("data-ctr-active");
            return;
          }

          source.setAttribute("data-ctr-active", navTitle);

          // Overflow items: panel is inside the submenu — theme handles visibility
          if (li.slot === "overflow") {
            // R32: hydrate via _panelMap, NOT source.querySelector -- an
            // overflow panel has been moved out of source into the submenu, so
            // querying source returns null. Caught on preview 14 Aug.
            const entry = this._panelMap && this._panelMap.get(navTitle);
            this._hydratePanel(entry ? entry.panel : null);
            return;
          }

          // Main items: show panel via overlay
          const panel = source.querySelector(
            `.ctr-mega-menu-source__panel[data-nav-title="${navTitle}"]`
          );
          if (panel) {
            this._hydratePanel(panel); // R32: must precede the offsetHeight read below
            panel.setAttribute("data-ctr-visible", "");
            requestAnimationFrame(() => {
              const h = panel.offsetHeight;
              headerMenu.style.setProperty("--submenu-height", `${h}px`);
            });
          }
        };

        this._observer = new MutationObserver(syncPanel);
        this._observer.observe(headerMenu, {
          subtree: true,
          attributes: true,
          attributeFilter: ["aria-expanded"],
        });
      },

      /* Item 90: everything _setup() and _observe() attached, undone, with the
         overflow panels moved back into the source. */
      _teardown() {
        if (this._observer) this._observer.disconnect();
        if (this._slotObserver) this._slotObserver.disconnect();

        // Move any overflow panels back to source before cleanup
        if (this._panelMap) {
          for (const [navTitle] of this._panelMap) {
            this._moveToSource(navTitle);
          }
        }

        if (this.$el.__ctrMegaPointerAbort) this.$el.__ctrMegaPointerAbort.abort();
        this._clearSubmenuHeight();
        this.$el.removeAttribute("data-ctr-mounted");
      },

      /* Item 90: take the source home to its own section instead of losing it with a
         re-rendered header. markParked is for the unload path, where the component stays
         alive and section:load remounts it; the destroy path re-initialises instead. */
      _park(markParked) {
        const home = this.$el.__ctrMegaHome;
        if (!home || !home.isConnected || home.contains(this.$el)) return false;
        this._teardown();
        if (markParked) this.$el.setAttribute("data-ctr-parked", "");
        home.appendChild(this.$el);
        return true;
      },

      _remount() {
        const headerMenu = document.querySelector("header-menu");
        if (!headerMenu || this.$el.parentElement === headerMenu) return;
        this.$el.removeAttribute("data-ctr-parked");
        this._setup();
        this._observe();
        this._refreshScrollComponents();
      },

      destroy() {
        if (
          typeof Shopify !== "undefined" &&
          Shopify.designMode &&
          !this.$el.isConnected &&
          this._park(false)
        ) {
          return;
        }

        this._teardown();
        ctrMegaCleanupHeader();
      },
    };
  });
});
